import { useId, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { accessRequestsApi, gcpApi, resourcesApi } from '../services/api';
import { AccessRequest, Resource } from '../types';
import { isActiveApproval, requestMatchesGcpProject, resourceMatchesGcpProject } from '@/lib/accessGrants';
import { Plus, Earth, RefreshCw, ExternalLink, Shield, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import { usePermissions } from '../hooks/usePermissions';
import AccessRequestForm from '../components/AccessRequestForm';
import { toast } from '@/lib/toast';
import { Link } from 'react-router-dom';
import { Banner, Breadcrumbs, Button, Card, EmptyState, FileUpload, Input, Modal, PageHeader, Select, StatusBadge, OutcomeBadge, MonoCell } from '@/components/ds-zta';
import { BRAND } from '@/brand';
import ProviderMark from '@/components/ProviderMark';
import CardGridSkeleton from '@/components/CardGridSkeleton';

export default function GCPResources() {
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showAccessRequestForm, setShowAccessRequestForm] = useState(false);
  const [selectedResourceForRequest, setSelectedResourceForRequest] = useState<Resource | null>(null);
  const [requestProject, setRequestProject] = useState<any>(null);
  const [showRoleMappings, setShowRoleMappings] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const queryClient = useQueryClient();
  const { isAdmin } = usePermissions();
  const isUserAdmin = isAdmin();

  // Fetch projects
  const { data: projectsData, isLoading: projectsLoading, isError: listError, refetch: refetchList } = useQuery('gcp-projects', () => gcpApi.listProjects());
  const projects = projectsData?.data?.projects || [];

  // F21: real access checks for non-admins, from data the frontend already has.
  //   1. GCP-typed /resources rows that belong to each project, and their
  //      /resources/:id/access-status (server-evaluated grant), and
  //   2. the user's own approved, unexpired requests for that project.
  // Either is enough to show "Open GCP Console"; the console-url call is still
  // authorised by the server, so a stale answer here can only show a button that
  // then fails with a toast, never grant access.
  const { data: gcpResources = [] } = useQuery(
    ['gcp-access-resources'],
    () => resourcesApi.listAll(undefined, undefined, 200, 0)
      .then(res => (res.data?.resources || []).filter((r: Resource) => String(r.type).startsWith('gcp'))),
    { enabled: !isUserAdmin },
  );

  const { data: myApprovalsData } = useQuery(
    ['my-approvals-gcp'],
    () => accessRequestsApi.getMyApprovals(),
    { enabled: !isUserAdmin, refetchInterval: 30000 },
  );
  const myApprovals = useMemo<AccessRequest[]>(() => myApprovalsData?.data?.items || [], [myApprovalsData]);

  const projectResources = useMemo(() => {
    const map: Record<string, Resource[]> = {};
    for (const proj of projects) map[proj.id] = gcpResources.filter((r: Resource) => resourceMatchesGcpProject(r, proj));
    return map;
  }, [projects, gcpResources]);

  const { data: accessStatusMap } = useQuery(
    ['gcp-access-status', Object.values(projectResources).flat().map(r => r.id).join(',')],
    async () => {
      const statusMap: Record<string, boolean> = {};
      await Promise.all(Object.entries(projectResources).map(async ([projId, resources]) => {
        for (const r of resources) {
          try {
            const resp = await resourcesApi.getAccessStatus(r.id);
            if (resp.data.has_access === true) { statusMap[projId] = true; return; }
          } catch { /* treated as no access */ }
        }
        statusMap[projId] = false;
      }));
      return statusMap;
    },
    { enabled: !isUserAdmin && Object.values(projectResources).some(list => list.length > 0), refetchInterval: 30000 },
  );

  // Console URL mutation
  const consoleUrlMutation = useMutation(
    (data: { project_id: string; destination_url?: string; role_name?: string; resource_type?: string; resource_id?: string }) => gcpApi.generateConsoleUrl({
      project_id: data.project_id,
      destination_url: data.destination_url || 'https://console.cloud.google.com/',
      role_name: data.role_name || '',
      resource_type: data.resource_type || '',
      resource_id: data.resource_id || ''
    }),
    {
      onSuccess: (res) => { window.open(res.data.console_url, '_blank'); toast.success('GCP Console opened'); },
      onError: (err: any) => { toast.error(err?.response?.data?.error || 'Failed to open console'); },
    }
  );

  // Project CRUD
  const createProjectMutation = useMutation((data: any) => gcpApi.createProject(data), {
    onSuccess: () => { toast.success('GCP project added'); queryClient.invalidateQueries('gcp-projects'); setShowProjectForm(false); },
    onError: () => { toast.error('Failed to add project'); },
  });

  // Role mappings (fetch always for role picker)
  const { data: roleMappingsData } = useQuery('gcp-role-mappings', () => gcpApi.listRoleMappings());
  const roleMappings = roleMappingsData?.data?.mappings || [];

  const handleOpenConsole = (project: any, roleName?: string) => {
    consoleUrlMutation.mutate({
      project_id: project.project_id,
      destination_url: `https://console.cloud.google.com/?project=${project.project_id}`,
      ...(roleName ? { role_name: roleName } : {}),
    });
  };

  // Opens the shared request form, pre-selecting the project's resource when one
  // exists; otherwise the form starts at its resource picker.
  const handleRequestAccess = (project: any) => {
    setSelectedResourceForRequest(projectResources[project.id]?.[0] ?? null);
    setRequestProject(project);
    setShowAccessRequestForm(true);
  };

  const userHasAccess = (project: any): boolean => {
    if (isUserAdmin) return true;
    if (accessStatusMap?.[project.id]) return true;
    const ids = new Set((projectResources[project.id] || []).map(r => r.id));
    return myApprovals.some(req => isActiveApproval(req) && requestMatchesGcpProject(req, project, ids));
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <PageHeader
        className="mb-6"
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: 'Cloud Providers', href: '/cloud-providers' }, { label: 'Google Cloud Platform' }]}
            renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
          />
        }
        icon={<Earth />}
        title="Google Cloud Platform"
        description={`Access the GCP Console through ${BRAND.name}`}
        secondaryActions={isAdmin() ? (
          <Button variant="secondary" aria-pressed={showRoleMappings} onClick={() => setShowRoleMappings(!showRoleMappings)}
            leadingIcon={<Shield className="h-4 w-4" />}
            className={clsx(showRoleMappings && 'ring-2 ring-[var(--ds-color-ring)]')}>
            GCP Roles
          </Button>
        ) : undefined}
        primaryAction={isAdmin() ? (
          <Button onClick={() => setShowProjectForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
            Add Project
          </Button>
        ) : undefined}
      />

      {/* Project Cards */}
      {projectsLoading ? (
        <CardGridSkeleton label="Loading GCP projects" />
      ) : listError ? (
        <Card>
          <EmptyState
            icon={<Earth />}
            title="Could not load GCP projects"
            description="Something went wrong while fetching the list. Check your connection and try again."
            action={<Button variant="secondary" onClick={() => refetchList()} leadingIcon={<RefreshCw className="h-4 w-4" />}>Retry</Button>}
          />
        </Card>
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Earth />}
            title="No GCP Projects Configured"
            description={`Add a GCP project to enable console access through ${BRAND.name}.`}
            action={isAdmin() ? (
              <Button onClick={() => setShowProjectForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                Add Project
              </Button>
            ) : undefined}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((proj: any) => {
            const hasAccess = userHasAccess(proj);
            return (
              <Card key={proj.id} className="group">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <ProviderMark label="GCP" />
                      <div className="min-w-0">
                        <h3 className="text-base font-medium text-[var(--d2d-text-primary)]">{proj.project_name}</h3>
                        <p className="text-xs text-[var(--d2d-text-muted)]"><MonoCell value={String(proj.project_id ?? '')} /></p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg bg-[var(--d2d-bg-secondary)]">
                    {hasAccess ? (
                      <StatusBadge value="approved">Access Granted</StatusBadge>
                    ) : (
                      <OutcomeBadge value="required" tone="new">Access Required</OutcomeBadge>
                    )}
                  </div>

                  {hasAccess && (
                    <Button className="w-full" onClick={() => handleOpenConsole(proj)} loading={consoleUrlMutation.isLoading}
                      leadingIcon={consoleUrlMutation.isLoading ? undefined : <ExternalLink className="h-4 w-4" />}>
                      Open GCP Console
                    </Button>
                  )}

                  {!hasAccess && (
                    <Button variant="secondary" className="w-full" onClick={() => handleRequestAccess(proj)}
                      leadingIcon={<Clock className="h-4 w-4" />}>
                      Request Access
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Role Mappings Panel (admin only) */}
      {showRoleMappings && isAdmin() && (
        <Card className="mt-8">
          <Card.Header
            title="Available GCP Roles"
            description="GCP Service Accounts/Roles that users can request access to"
            actions={
              <Button onClick={() => setShowAddMapping(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                Add Role
              </Button>
            }
          />
          <div className="divide-y divide-[var(--d2d-border)]">
            {roleMappings.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-[var(--d2d-text-muted)]">
                No GCP roles configured. Add roles that users can request access to.
              </div>
            ) : (
              roleMappings.map((m: any) => (
                <div key={m.id} className="px-6 py-4 flex items-center justify-between hover:bg-[var(--ds-color-bg-hover)]">
                  <div className="flex items-center gap-3 min-w-0">
                    <Shield aria-hidden className="h-4 w-4 flex-none text-[var(--d2d-text-muted)]" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[var(--d2d-text-primary)]">
                        {m.gcp_role_name}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {/* Info Banner */}
      <Banner variant="info" title="How GCP Access Works" className="mt-8">
        {isAdmin()
          ? 'As an admin, you can access the GCP Console directly. Use GCP Roles to register specific IAM roles or Service Accounts that users can request access to.'
          : 'Click "Request Access" to submit an access request. Once an admin approves it, and for as long as the grant lasts, "Open GCP Console" appears on the project, with permissions defined by your role mapping.'}
      </Banner>

      {/* Modals */}
      {showProjectForm && (
        <AddProjectModal onClose={() => setShowProjectForm(false)}
          onSubmit={(data) => createProjectMutation.mutate(data)} isLoading={createProjectMutation.isLoading} />
      )}

      {showAddMapping && (
        <AddRoleMappingModal projects={projects} onClose={() => setShowAddMapping(false)}
          onSuccess={() => { queryClient.invalidateQueries('gcp-role-mappings'); setShowAddMapping(false); }} />
      )}

      {showAccessRequestForm && (
        <AccessRequestForm resource={selectedResourceForRequest}
          metadata={requestProject ? { gcp_project_id: requestProject.project_id } : undefined}
          onClose={() => { setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setRequestProject(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries('my-approvals-gcp');
            queryClient.invalidateQueries('gcp-access-status');
            setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setRequestProject(null);
          }}
        />
      )}
    </div>
  );
}

// ===== Add Project Modal =====
function AddProjectModal({ onClose, onSubmit, isLoading }: { onClose: () => void; onSubmit: (data: any) => void; isLoading: boolean }) {
  const formId = useId();
  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [serviceAccountEmail, setServiceAccountEmail] = useState('');
  // The service-account key is a secret (design eval F16, decision D4 = A):
  // it is read from the key file, or pasted into a masked field, and never
  // drawn on screen.
  const [credentialsJson, setCredentialsJson] = useState('');
  const [keyFiles, setKeyFiles] = useState<File[]>([]);
  const [keyError, setKeyError] = useState('');

  const readKeyFile = async (files: File[]) => {
    setKeyFiles(files);
    setKeyError('');
    if (files.length === 0) { setCredentialsJson(''); return; }
    try {
      setCredentialsJson(await files[0].text());
    } catch {
      setCredentialsJson('');
      setKeyError("Couldn't read that file.");
    }
  };

  const credentialsInvalid = (() => {
    if (!credentialsJson.trim()) return false;
    try { JSON.parse(credentialsJson); return false; } catch { return true; }
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (credentialsInvalid) return;
    onSubmit({
      project_id: projectId, project_name: projectName,
      service_account_email: serviceAccountEmail,
      credentials_json: credentialsJson || undefined,
    });
  };

  return (
    <Modal open onClose={onClose} title="Add GCP Project" size="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={isLoading || credentialsInvalid}>{isLoading ? 'Adding...' : 'Add Project'}</Button>
        </>
      }>
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <Input label="Project ID" placeholder="my-gcp-project-123" value={projectId} onChange={(e) => setProjectId(e.target.value)} required />
        <Input label="Project Name" placeholder="Production Project" value={projectName} onChange={(e) => setProjectName(e.target.value)} required />
        <Input label="Service Account Email" className="[&_input]:font-mono" placeholder="iap-agent@my-gcp-project.iam.gserviceaccount.com" value={serviceAccountEmail} onChange={(e) => setServiceAccountEmail(e.target.value)} required />
        <FileUpload
          label="Service account key file (optional)"
          hint="The JSON key file from GCP. It is sent to the server and not shown here."
          accept=".json,application/json"
          maxSize={64 * 1024}
          value={keyFiles}
          onChange={readKeyFile}
          onReject={() => setKeyError('Choose a JSON key file under 64 KB.')}
          error={keyError || (keyFiles.length > 0 && credentialsInvalid ? 'That file is not valid JSON.' : undefined)}
        />
        {keyFiles.length === 0 && (
          <Input
            label="Or paste the key JSON (optional)"
            type="password"
            autoComplete="off"
            spellCheck={false}
            className="[&_input]:font-mono"
            placeholder="Paste the JSON key"
            value={credentialsJson}
            onChange={(e) => setCredentialsJson(e.target.value)}
            error={credentialsInvalid ? 'Not valid JSON.' : undefined}
          />
        )}
      </form>
    </Modal>
  );
}

// ===== Add Role Mapping Modal =====
function AddRoleMappingModal({ projects, onClose, onSuccess }: { projects: any[]; onClose: () => void; onSuccess: () => void }) {
  const formId = useId();
  const [gcpProjectId, setGcpProjectId] = useState(projects[0]?.id || '');
  const [gcpRoleName, setGcpRoleName] = useState('');
  const [maxDuration, setMaxDuration] = useState('3600');

  const createMutation = useMutation(
    () => gcpApi.createRoleMapping({ gcp_project_id: gcpProjectId, gcp_role_name: gcpRoleName, max_session_duration: parseInt(maxDuration) }),
    { onSuccess: () => { toast.success('Mapping created'); onSuccess(); }, onError: () => { toast.error('Failed to create mapping'); } }
  );

  return (
    <Modal open onClose={onClose} title="Add GCP Role" size="md"
      description="Register an GCP Service Account that users can request access to"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={createMutation.isLoading}>
            {createMutation.isLoading ? 'Creating...' : 'Add Role'}
          </Button>
        </>
      }>
      <form id={formId} onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="space-y-4">
        <Select label="GCP Project" value={gcpProjectId} onChange={(e) => setGcpProjectId(e.target.value)} required>
          {projects.map((a: any) => <option key={a.id} value={a.id}>{a.project_name} ({a.project_id})</option>)}
        </Select>
        <Input label="Service Account / Role Name" className="[&_input]:font-mono" placeholder="viewer-sa@project.iam.gserviceaccount.com" value={gcpRoleName} onChange={(e) => setGcpRoleName(e.target.value)} required />
        <Input label="Max Session Duration (seconds)" type="number" min="900" max="43200" value={maxDuration} onChange={(e) => setMaxDuration(e.target.value)} />
      </form>
    </Modal>
  );
}
